import React, { useState, useEffect } from 'react';
import { DisplayParams } from "../types/classes/DisplayParams";
import { Link, MenuItem, Stack, TextField, Typography, Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { CphResource } from '../resources/CphResource';
import { useCphDataContext } from '../contexts/CphDataContext';
import { useCphContext } from '../contexts/CphContext';

interface FiltersProps {
    displayParams: DisplayParams;
    onDisplayChange: (displayParams: DisplayParams) => void;
}


// Example filter state (replace with your actual state management as needed)
type CheckboxName = 'filterAll' | 'filterNew' | 'filterInProgress' | 'filterCompleted' | 'filterClosed' | 'filterPayer' | 'filterCoding';

type SavedFiltersState = {
    checkboxes: Record<CheckboxName, boolean>;
    reporterName: string;
    staffName: string;
    selectedPriority: string;
};

const FILTERS_STORAGE_KEY = 'cph.filters.state';

const initialCheckboxes: Record<CheckboxName, boolean> = {
    filterAll: true,
    filterNew: false,
    filterInProgress: false,
    filterCompleted: false,
    filterClosed: false,
    filterPayer: false,
    filterCoding: false,
};


const Filters: React.FC<FiltersProps> = ({ displayParams, onDisplayChange }) => {
    const [checkboxes, setCheckboxes] = useState(initialCheckboxes);
    const [reporterName, setReporterName] = useState("");
    const [staffName, setStaffName] = useState("");
    const [selectedPriority, setSelectedPriority] = useState("");
    const [isStorageLoaded, setIsStorageLoaded] = useState(false);
    const { isStaff, prioritiesList } = useCphContext();

    const { originalIssuesList, setFilteredIssuesList, setFiltersHydrated } = useCphDataContext();

    useEffect(() => {
        setFiltersHydrated(false);
        try {
            const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY);
            if (raw) {
                const parsed = JSON.parse(raw) as Partial<SavedFiltersState>;
                if (parsed.checkboxes) {
                    setCheckboxes({ ...initialCheckboxes, ...parsed.checkboxes });
                }
                if (typeof parsed.reporterName === 'string') {
                    setReporterName(parsed.reporterName);
                }
                if (typeof parsed.staffName === 'string') {
                    setStaffName(parsed.staffName);
                }
                if (typeof parsed.selectedPriority === 'string') {
                    setSelectedPriority(parsed.selectedPriority);
                }
            }
        } catch {
            // Ignore invalid session payload and keep defaults.
        } finally {
            setIsStorageLoaded(true);
        }
    }, []);


    // Central filter application function
    const applyAllFilters = (checkboxState: Record<CheckboxName, boolean>, reporter: string, staff: string, priority: string) => {
        if (!originalIssuesList) {
            setFilteredIssuesList([]);
            onDisplayChange(new DisplayParams());
            return;
        }
        let filtered = [...originalIssuesList];

        // Status checkboxes (can be multi-selected)
        const statusFilters: string[] = [];
        if (checkboxState.filterNew) statusFilters.push("new");
        if (checkboxState.filterInProgress) statusFilters.push("in progress");
        if (checkboxState.filterCompleted) statusFilters.push("completed");
        if (checkboxState.filterClosed) statusFilters.push("closed");
        if (statusFilters.length > 0) {
            filtered = filtered.filter(issue => statusFilters.includes(issue.status?.status?.toLowerCase() as string));
        }

        if (checkboxState.filterPayer) {
            filtered = filtered.filter(issue => issue.payerIssues && issue.payerIssues.length > 0);
        }
        if (checkboxState.filterCoding) {
            filtered = filtered.filter(issue => issue.codingIssues && issue.codingIssues.length > 0);
        }

        // Text filters
        if (reporter.length >= 3) {
            const search = reporter.toLowerCase();
            filtered = filtered.filter(issue => {
                const first = issue.reporter?.firstName?.toLowerCase() || "";
                const last = issue.reporter?.lastName?.toLowerCase() || "";
                return first.includes(search) || last.includes(search);
            });
        }
        if (staff.length >= 3) {
            const search = staff.toLowerCase();
            filtered = filtered.filter(issue => {
                if (!issue.assignedTo || !Array.isArray(issue.assignedTo)) return false;
                return issue.assignedTo.some(person => {
                    const first = person?.firstName?.toLowerCase() || "";
                    const last = person?.lastName?.toLowerCase() || "";
                    return first.includes(search) || last.includes(search);
                });
            });
        }
        if (priority.length > 0) {
            const search = priority.toLowerCase();
            filtered = filtered.filter(issue => {
                if (!issue.priority) return false;
                return issue.priority.toLowerCase() === search;
            });
        }

        setFilteredIssuesList(filtered);
        onDisplayChange(new DisplayParams());
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;
        let newCheckboxes = { ...checkboxes };
        const checkboxName = name as CheckboxName;

        switch (checkboxName) {
            case "filterAll":
                if (checked) {
                    newCheckboxes = {
                        ...newCheckboxes,
                        filterAll: true,
                        filterNew: false,
                        filterInProgress: false,
                        filterCompleted: false,
                        filterPayer: false,
                        filterCoding: false,
                    };
                } else {
                    newCheckboxes.filterAll = false;
                }
                break;
            case "filterNew":
            case "filterInProgress":
            case "filterCompleted":
            case "filterClosed":
                if (checked) {
                    newCheckboxes = {
                        ...newCheckboxes,
                        [checkboxName]: true,
                        filterAll: false,
                    };
                } else {
                    newCheckboxes[checkboxName] = false;
                }
                break;
            case "filterPayer":
                newCheckboxes = {
                    ...newCheckboxes,
                    filterPayer: checked,
                    filterCoding: false,
                    filterAll: false,
                };
                break;
            case "filterCoding":
                newCheckboxes = {
                    ...newCheckboxes,
                    filterCoding: checked,
                    filterPayer: false,
                    filterAll: false,
                };
                break;
            default:
                newCheckboxes[checkboxName as CheckboxName] = checked;
        }
        // If all checkboxes are false, select filterAll
        const allDeselected =
            !newCheckboxes.filterNew &&
            !newCheckboxes.filterInProgress &&
            !newCheckboxes.filterCompleted &&
            !newCheckboxes.filterClosed &&
            !newCheckboxes.filterPayer &&
            !newCheckboxes.filterCoding;
        if (allDeselected) {
            newCheckboxes.filterAll = true;
        }
        setCheckboxes(newCheckboxes);
    };


    const ResetFilters = () => {
        setCheckboxes(initialCheckboxes);
        setReporterName("");
        setStaffName("");
        setSelectedPriority("");
        sessionStorage.removeItem(FILTERS_STORAGE_KEY);
        setFilteredIssuesList(originalIssuesList);
    };

    useEffect(() => {
        if (!isStorageLoaded) return;
        applyAllFilters(checkboxes, reporterName, staffName, selectedPriority);
        setFiltersHydrated(true);
    }, [checkboxes, reporterName, staffName, selectedPriority, originalIssuesList, isStorageLoaded, setFiltersHydrated]);

    useEffect(() => {
        if (!isStorageLoaded) return;
        const stateToSave: SavedFiltersState = {
            checkboxes,
            reporterName,
            staffName,
            selectedPriority,
        };
        sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(stateToSave));
    }, [checkboxes, reporterName, staffName, selectedPriority, isStorageLoaded]);

    const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.name === "reporterName") setReporterName(event.target.value);
        else if (event.target.name === "staffName") setStaffName(event.target.value);
        else if (event.target.name === "priority") setSelectedPriority(event.target.value);
    };

    return (
        <Stack spacing={2}>
            <Stack spacing={10} direction="row">
                <Typography variant="h4" color="primary.main" component="div" sx={{ ml: 0 }}>{CphResource.filter_lbl}</Typography>
                <Link color="secondary.dark" sx={{ fontSize: 18 }} component="button" onClick={ResetFilters} >{CphResource.reset_lbl}</Link>
            </Stack>
            <FormGroup>
                <FormControlLabel
                    control={<Checkbox checked={checkboxes.filterAll} onChange={handleCheckboxChange} name="filterAll" />}
                    label="All"
                />
                <FormControlLabel
                    control={<Checkbox checked={checkboxes.filterNew} onChange={handleCheckboxChange} name="filterNew" />}
                    label="New"
                />
                <FormControlLabel
                    control={<Checkbox checked={checkboxes.filterInProgress} onChange={handleCheckboxChange} name="filterInProgress" />}
                    label="In Progress"
                />
                <FormControlLabel
                    control={<Checkbox checked={checkboxes.filterCompleted} onChange={handleCheckboxChange} name="filterCompleted" />}
                    label="Completed"
                />
                <FormControlLabel
                    control={<Checkbox checked={checkboxes.filterClosed} onChange={handleCheckboxChange} name="filterClosed" />}
                    label="Closed"
                />
                {isStaff && (
                    <>
                        <FormControlLabel
                            control={<Checkbox checked={checkboxes.filterPayer} onChange={handleCheckboxChange} name="filterPayer" />}
                            label="Payer Issues"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={checkboxes.filterCoding} onChange={handleCheckboxChange} name="filterCoding" />}
                            label="Coding Issues"
                        />
                    </>
                )}
            </FormGroup>
            {isStaff && (
                <Stack spacing={2}>
                    <TextField
                        id="reporter_textbox"
                        label="Reporter Name"
                        variant="outlined"
                        name="reporterName"
                        value={reporterName}
                        onChange={handleTextChange}
                        sx={{ width: '90%', mb: 2 }}
                        InputLabelProps={{ sx: { fontSize: 16 } }}
                    />
                    <TextField
                        id="staff_textbox"
                        label="Staff Name"
                        variant="outlined"
                        name="staffName"
                        value={staffName}
                        onChange={handleTextChange}
                        sx={{ width: '90%', mb: 2 }}
                        InputLabelProps={{ sx: { fontSize: 16 } }}
                    />
                    <TextField
                        id="priority_textbox"
                        label="Priority"
                        variant="outlined"
                        name="priority"
                        select
                        value={selectedPriority}
                        onChange={handleTextChange}
                        sx={{ width: '90%', mb: 2 }}
                        InputLabelProps={{ sx: { fontSize: 16 } }}
                    >
                        <MenuItem value="">All Priorities</MenuItem>
                        {(prioritiesList ?? []).map((priorityItem) => (
                            <MenuItem
                                key={priorityItem.Code ?? `${priorityItem.Key ?? ''}`}
                                value={priorityItem.Code ?? ''}
                            >
                                {priorityItem.Code ?? 'Unknown Priority'}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>)
            }
        </Stack>
    );
}
export default Filters;